import React, { createContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { ActionItem, ContentState, ContentSubstate, getContentAdminActionableItems, getContentHistoryStatusString, Tier } from "../models/SKArtifact";

import { SKArtifactPayload, getContentCreatorActionableItems } from "../models/SKArtifact";
import {
  getArtifact,
  getArtifactEdit,
  createArtifactEdit,
  updateArtifact,
  updateArtifactEdit,
  updateArtifactStatus,
  updateStatusHistory,
  updateArtifactEditStatus,
  deleteArtifact,
  deleteArtifactEdit,
} from "../api/artifacts/atrifactService";
import { useUpdateList } from "../pages/contentModeration/UpdateListContext";
import { useAuth } from "./AuthContext";
import { Tier as ChannelTier } from "../models/SKChannel";

export interface CreateArtifactContextType {
  contentData: SKArtifactPayload;
  updateContentData: (data: any) => void;
  loading: boolean;
  setArtifactId: (artifactId: string) => void;
  artifactId?: string;
  saveData: () => Promise<void>;
  submit: () => Promise<void>;
  submitEdit: () => Promise<void>;
  updateStatus: (actionItem: ActionItem, reason?: string, note?: string) => Promise<void>;
  containerButtonTitle: () => string;
  isModerator?: boolean; // to find out if user is Moderator
  isContentEditing?: boolean;
  isEditable?: boolean;
  doneSubmitting?: boolean; // to check if Content Submitted for review to navigate to SuccessSbmit view
  resetSubmitting?: () => void;
  clearUnsavedChanges?: () => void;
  getContentActionableCreatorItems: () => ActionItem[];
  getContentActionableAdminItems: () => ActionItem[];
  originalState?: { status: ContentState; subStatus: ContentSubstate };
  unsavedChanges: boolean; // to check if document is saved or not, if not saved show the modal dialog to save if navigating away of the page
}

// export const AuthContext = createContext<IAuth>(defaultValue);
const signupInitialState: CreateArtifactContextType = {
  setArtifactId: () => {},
  contentData: {
    type: null,
    text: "",
    certifyFirst: false,
    certifySecond: false,
    tier: Tier.free,
    //autoPublish: false,
    title: "",
    subtitle: "",
    status: ContentState.DRAFT,
    subStatus: ContentSubstate.NONE,
  },
  updateContentData: () => {}, // Provide a no-op function for the initial updateFormData
  loading: false,
  saveData: async () => {},
  submit: async () => {},
  submitEdit: async () => {},
  updateStatus: async (actionItem: ActionItem, reason?: string, note?: string) => {},
  containerButtonTitle: () => "",
  getContentActionableCreatorItems: () => [],
  getContentActionableAdminItems: () => [],
  unsavedChanges: false,
  // createApi: async () => false, // Provide a no-op async function that returns false
};

export const CreateArtifactContext = createContext<CreateArtifactContextType>(signupInitialState);

interface CreateArtifactProviderProps {
  children: ReactNode;
  isModerator?: boolean;
  isContentEditing?: boolean;
}

const CreateArtifactProvider = ({ children, isModerator = false, isContentEditing = false }: CreateArtifactProviderProps) => {
  const [contentData, setContentData] = useState<SKArtifactPayload>({
    type: null,
    text: "",
    certifyFirst: false,
    certifySecond: false,
    tier: Tier.free,
    //autoPublish: false,
    title: "",
    subtitle: "",
    status: ContentState.DRAFT,
    subStatus: ContentSubstate.NONE,
  });

  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [artifactId, setArtifactId] = useState<string | undefined>(undefined);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [doneSubmitting, setDoneSubmitting] = useState<boolean>(false);
  const contentDataRef = useRef(contentData);
  const artifactIdRef = useRef(artifactId);
  const [originalState, setOriginalState] = useState<{ status: ContentState; subStatus: ContentSubstate } | undefined>(undefined);
  const { updateList } = useUpdateList();

  const authContext = useAuth();

  const clearUnsavedChanges = () => {
    setUnsavedChanges(false);
  };

  const saveData = async () => {
    let id = artifactIdRef.current;
    let { status, subStatus, channelStatus, ...artifactData } = contentDataRef.current;
    if (id && isEditable && artifactData.type && artifactData.title !== "") {
      setLoading(true);
      isContentEditing ? await updateArtifactEdit(id, artifactData) : await updateArtifact(id, artifactData);
      await setUnsavedChanges(false);
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const channelSubscriptionStatus = context?.myChannel?.subscriptionStatus;

  //   if (channelSubscriptionStatus === ChannelTier.paid) {
  //     setContentData(prev => ({ ...prev, tier: Tier.subscription }));
  //   }
  // }, [contentDataRef, context]);

  const containerButtonTitle = useCallback((): string => {
    const state = contentData.status;
    switch (state) {
      case ContentState.DRAFT:
        return "Submit";
      case ContentState.ACTIVE:
        return "Edit";
      // case State.published:
      //   return "Withdraw";
      default:
        break;
    }
    return "";
  }, [contentData.status]);

  const submit = async (): Promise<void> => {
    let id = artifactIdRef.current;
    const state = contentDataRef.current.status;
    let artifactData = { ...contentDataRef.current };
    if (!(id && artifactData.type && artifactData.title !== "")) throw Error("Failed to Submit. Try again");

    switch (state) {
      case ContentState.DRAFT:
        artifactData = { ...contentDataRef.current, status: ContentState.IN_REVIEW, subStatus: ContentSubstate.NONE };
        break;
      case ContentState.ACTIVE:
        artifactData = { ...contentDataRef.current, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_IN_REVIEW };
        break;
      default:
        break;
    }

    try {
      setLoading(true);
      await updateArtifact(id, artifactData);
      await updateStatusHistory(id, getContentHistoryStatusString(artifactData.status, artifactData.subStatus));
      updateList(id, artifactData.status, artifactData.subStatus);
      setContentData(artifactData);
      setUnsavedChanges(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw Error("Failed to Submit. Try again");
    }
  };

  const submitEdit = async (): Promise<void> => {
    let id = artifactIdRef.current;
    const data = contentDataRef.current;
    let artifactData = { ...data, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_IN_REVIEW };
    if (!(id && artifactData.type && artifactData.title !== "")) throw Error("Failed to Submit. Try again");
    try {
      setLoading(true);
      await updateArtifactEdit(id, artifactData);
      await updateArtifactEditStatus(id, ContentState.ACTIVE, ContentSubstate.EDIT_IN_REVIEW);
      setContentData(artifactData);
      setUnsavedChanges(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw Error("Failed to Submit. Try again");
    }
  };

  const updateStatus = async (actionItem: ActionItem, reason?: string, note?: string) => {
    if (!artifactId) return;
    switch (actionItem) {
      case ActionItem.delete:
        await deleteArtifact(artifactId);
        break;
      case ActionItem.approve:
        await updateArtifactStatus(artifactId, ContentState.ACTIVE, ContentSubstate.NONE, reason, note);
        updateList(artifactId, ContentState.ACTIVE, ContentSubstate.NONE);
        setContentData(prev => ({ ...prev, status: ContentState.ACTIVE, subStatus: ContentSubstate.NONE }));
        break;
      case ActionItem.withdraw:
        //await updateArtifact(contentId, { status: State.rejected });
        await updateArtifactStatus(artifactId, ContentState.DRAFT, ContentSubstate.WITHDRAWN, reason, note);
        updateList(artifactId, ContentState.DRAFT, ContentSubstate.WITHDRAWN);
        setContentData(prev => ({ ...prev, status: ContentState.DRAFT, subStatus: ContentSubstate.WITHDRAWN }));
        await deleteArtifactEdit(artifactId);
        break;
      case ActionItem.reject:
        //await updateArtifact(contentId, { status: State.rejected });
        await updateArtifactStatus(artifactId, ContentState.DRAFT, ContentSubstate.REJECTED, reason, note);
        updateList(artifactId, ContentState.DRAFT, ContentSubstate.REJECTED);
        setContentData(prev => ({ ...prev, status: ContentState.DRAFT, subStatus: ContentSubstate.REJECTED }));
        await deleteArtifactEdit(artifactId);
        break;
      case ActionItem.reject_edit:
        await updateArtifactEditStatus(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED, reason, note);
        updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED);
        setContentData(prev => ({ ...prev, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_REJECTED }));

        break;
      case ActionItem.approve_edit:
        try {
          const { status, subStatus, ...rest } = contentData;
          await updateArtifact(artifactId, { ...rest, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_APPROVED });
          await updateStatusHistory(
            artifactId,
            getContentHistoryStatusString(ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED, true),
            reason,
            note
          );
          await deleteArtifactEdit(artifactId);
          updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED);
          setContentData(prev => ({ ...prev, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_APPROVED }));
        } catch (error) {
          console.log(error);
        }
        break;
    }
  };

  // const updateStatus2 = async (actionItem: ActionItem, reason?: string, note?: string) => {
  //   if (!artifactId) return;

  //   var data: any = {};
  //   switch (actionItem) {
  //     case ActionItem.delete:
  //       deleteArtifact(artifactId);
  //       break;
  //     case ActionItem.approve:
  //       await updateArtifactStatus(artifactId, ContentState.ACTIVE, ContentSubstate.NONE, reason, note);
  //       // await updateArtifact(contentId, { status: content?.autoPublish ? State.published : State.approved });
  //       //updateLocalStatus(artifactId, contentData?.autoPublish ? State.published : State.approved);
  //       updateList(artifactId, ContentState.ACTIVE, ContentSubstate.NONE);
  //       updateContentData({
  //         status: ContentState.ACTIVE,
  //         subStatus: ContentSubstate.NONE,
  //       });
  //       break;
  //     case ActionItem.reject:
  //       //await updateArtifact(contentId, { status: State.rejected });
  //       await updateArtifactStatus(artifactId, ContentState.DRAFT, ContentSubstate.REJECTED, reason, note);
  //       updateContentData({ status: ContentState.DRAFT, subStatus: ContentSubstate.REJECTED });
  //       updateList(artifactId, ContentState.DRAFT, ContentSubstate.REJECTED);
  //       break;

  //     //   case ActionItem.reject_edit:
  //     //     // await updateArtifact(contentId, { status: State.blocked });
  //     //     await updateArtifactStatus(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED, reason, note);
  //     //     updateContentData({ status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_REJECTED });
  //     //     updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED);
  //     //     break;
  //     //   case ActionItem.approve_edit:
  //     //     // await updateArtifact(contentId, { status: State.blocked });
  //     //     await updateArtifactStatus(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED, reason, note);
  //     //     updateContentData({ status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_APPROVED });
  //     //     updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED);
  //     //     break;

  //     case ActionItem.approve_edit:
  //       //await updateChannelStatus(contentId, ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED);

  //       try {
  //         const { status, subStatus, ...rest } = contentData;
  //         await updateArtifact(artifactId, { ...rest, status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_APPROVED });
  //         await updateStatusHistory(
  //           artifactId,
  //           getContentHistoryStatusString(ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED, true),
  //           reason,
  //           note
  //         );
  //         await deleteArtifactEdit(artifactId);
  //         updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_APPROVED);
  //         updateContentData({ status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_APPROVED });
  //       } catch (error) {
  //         console.log(error);
  //       }

  //       break;
  //     case ActionItem.reject_edit:
  //       await updateArtifactEditStatus(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED, reason, note);
  //       updateList(artifactId, ContentState.ACTIVE, ContentSubstate.EDIT_REJECTED);
  //       updateContentData({ status: ContentState.ACTIVE, subStatus: ContentSubstate.EDIT_REJECTED });

  //       break;
  //   }
  // };

  const updateContentData = (newData: any) => {
    setContentData(prevData => {
      const updatedData = { ...prevData, ...newData };
      return Object.fromEntries(Object.entries(updatedData).filter(([_, value]) => value !== undefined)) as unknown as SKArtifactPayload;
    });
    setUnsavedChanges(true);
  };

  useEffect(() => {
    artifactIdRef.current = artifactId;
  }, [artifactId]);

  useEffect(() => {
    contentDataRef.current = contentData;
  }, [contentData]);

  // useEffect(() => {
  //   return () => {
  //     saveData();
  //   };
  // }, []);

  const resetSubmitting = () => {
    setDoneSubmitting(false);
  };

  const getContentActionableCreatorItems = useCallback((): ActionItem[] => {
    const { status, subStatus } = contentData;
    const actions = getContentCreatorActionableItems(status, subStatus);
    return actions || [];
  }, [contentData]);

  const getContentActionableAdminItems = (): ActionItem[] => {
    // const { status, subStatus } = contentData;

    const actions = getContentAdminActionableItems(contentData.status, contentData.subStatus);
    // if (contentData.flagged) {
    //   let data = [ActionItem.keep_published];
    //   data.push(...actions[contentData.status]);
    //   return data || [];
    // }
    return actions || [];
  };

  useEffect(() => {
    !isModerator &&
    (isContentEditing
      ? originalState && originalState.status === ContentState.ACTIVE && originalState.subStatus !== ContentSubstate.EDIT_IN_REVIEW
      : contentData.status === ContentState.DRAFT)
      ? setIsEditable(true)
      : setIsEditable(false);
  }, [contentData, originalState]);

  useEffect(() => {
    const getArtifactLocal = async (artifactId: string) => {
      console.log("fetching artifacts .....");
      setLoading(true);
      let artifact = await getArtifact(artifactId);
      setContentData(artifact);
      setLoading(false);
    };

    const getArtifactEditLocal = async (artifactId: string) => {
      console.log("fetching artifacts .....");

      try {
        const { status, subStatus } = await getArtifact(artifactId);
        const channelnew = await getArtifactEdit(artifactId);
        setOriginalState({ status, subStatus });
        setContentData(channelnew);
      } catch (error) {
        const channelnew = await getArtifact(artifactId);
        const { id, flagged, loveCount, likeCount, shareCount, views, bookmarkCount, viewCount, reportCount, lastReportedDate, ...rest } = channelnew;
        const channelEdits = await createArtifactEdit(artifactId, rest);
        setOriginalState({ status: channelEdits.status, subStatus: channelEdits.subStatus });
        setContentData(channelEdits);
      }
    };

    if (artifactId) {
      isContentEditing ? getArtifactEditLocal(artifactId) : getArtifactLocal(artifactId);
    }
  }, [artifactId, isContentEditing]);

  return (
    <CreateArtifactContext.Provider
      value={{
        artifactId,
        clearUnsavedChanges,
        submit,
        submitEdit,
        updateStatus,
        containerButtonTitle,
        saveData,
        setArtifactId,
        contentData,
        updateContentData,
        loading,
        unsavedChanges,
        isModerator,
        doneSubmitting,
        resetSubmitting,
        getContentActionableCreatorItems,
        getContentActionableAdminItems,
        originalState,
        isContentEditing,
        isEditable,
      }}
    >
      {children}
    </CreateArtifactContext.Provider>
  );
};

export default CreateArtifactProvider;
